import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { DollarSign, TrendingUp, TrendingDown, Briefcase, Package, Loader2 } from 'lucide-react';
import { TabsContent } from '../ui/tabs';

export const TabFinanceiro = ({ financeiroData, loadingFinanceiro, canViewServiceRevenue, canViewServicePayout, canViewStockCosts, isAdmin }) => (
  <TabsContent value="financeiro">
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Resumo Financeiro
        </CardTitle>
        <CardDescription>
          Custos de serviços e estoque para esta internação
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingFinanceiro ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : financeiroData ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {canViewServiceRevenue && financeiroData.resumo?.receita_servicos !== undefined && (
                <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Receita Serviços</p>
                    <p className="text-xl font-bold text-green-600">
                      R$ {financeiroData.resumo.receita_servicos?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
              )}
              {canViewServicePayout && financeiroData.resumo?.custo_servicos !== undefined && (
                <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Custo Serviços</p>
                    <p className="text-xl font-bold text-blue-600">
                      R$ {financeiroData.resumo.custo_servicos?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
              )}
              {canViewStockCosts && financeiroData.resumo?.custo_estoque !== undefined && (
                <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Custo Estoque</p>
                    <p className="text-xl font-bold text-amber-600">
                      R$ {financeiroData.resumo.custo_estoque?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
              )}
              {canViewServiceRevenue && canViewServicePayout && financeiroData.resumo?.custo_total !== undefined && (
                <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Custo Total</p>
                    <p className="text-xl font-bold text-purple-600">
                      R$ {financeiroData.resumo.custo_total?.toFixed(2) || '0.00'}
                    </p>
                  </CardContent>
                </Card>
              )}
              {!(canViewServiceRevenue || canViewServicePayout) && (
                <div className="col-span-2 md:col-span-4 p-4 text-center text-sm text-muted-foreground bg-muted/50 rounded-lg">
                  Valor restrito ao administrador master.
                </div>
              )}
            </div>
            
            {canViewServiceRevenue && canViewServicePayout && financeiroData.resumo?.lucro !== undefined && (
              <Card className={financeiroData.resumo.lucro >= 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-300' : 'bg-red-50 dark:bg-red-900/20 border-red-300'}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Lucro/Prejuízo</p>
                    <p className={`text-2xl font-bold ${financeiroData.resumo.lucro >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      R$ {financeiroData.resumo.lucro?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {financeiroData.resumo.lucro >= 0 ? (
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    ) : (
                      <TrendingDown className="h-6 w-6 text-red-600" />
                    )}
                    <span className={`text-lg font-semibold ${financeiroData.resumo.lucro >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {financeiroData.resumo.margem_percentual || 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4">
              <div className="flex gap-2 border-b">
                <button className="px-4 py-2 text-sm font-medium border-b-2 border-primary" data-testid="subtab-servicos">
                  <Briefcase className="h-4 w-4 inline mr-1" />
                  Serviços ({financeiroData.resumo?.total_servicos || 0})
                </button>
              </div>
              
              <div className="mb-6">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Serviços Realizados
                </h4>
                {financeiroData.servicos?.length > 0 ? (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="p-2 text-left">Serviço</th>
                          <th className="p-2 text-left">Data</th>
                          <th className="p-2 text-center">Qtd</th>
                          {isAdmin && <th className="p-2 text-right">Receita</th>}
                          {isAdmin && <th className="p-2 text-right">Custo</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {financeiroData.servicos.map((s) => (
                          <tr key={s.id} className="border-t">
                            <td className="p-2">{s.servico_nome}</td>
                            <td className="p-2">{s.data}</td>
                            <td className="p-2 text-center">{s.quantidade}</td>
                            {isAdmin && <td className="p-2 text-right text-green-600">R$ {(s.receita || 0).toFixed(2)}</td>}
                            {isAdmin && <td className="p-2 text-right text-blue-600">R$ {(s.custo || 0).toFixed(2)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 border rounded-lg">Nenhum serviço registrado</p>
                )}
              </div>
              
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Consumo de Estoque
                </h4>
                {financeiroData.estoque?.length > 0 ? (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="p-2 text-left">Item</th>
                          <th className="p-2 text-left">Data</th>
                          <th className="p-2 text-center">Qtd</th>
                          {isAdmin && <th className="p-2 text-right">Custo Unit.</th>}
                          {isAdmin && <th className="p-2 text-right">Custo Total</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {financeiroData.estoque.map((e) => (
                          <tr key={e.id} className="border-t">
                            <td className="p-2">{e.item_nome}</td>
                            <td className="p-2">{e.data}</td>
                            <td className="p-2 text-center">{e.quantidade}</td>
                            {isAdmin && <td className="p-2 text-right">R$ {(e.custo_unitario || 0).toFixed(2)}</td>}
                            {isAdmin && <td className="p-2 text-right text-amber-600">R$ {(e.custo_total || 0).toFixed(2)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 border rounded-lg">Nenhuma movimentação de estoque</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-4">
            Dados financeiros não disponíveis
          </p>
        )}
      </CardContent>
    </Card>
  </TabsContent>
);
